/**
 * AgentHubPage — full-width agent chat with a compact floating nav
 * (New chat + History) instead of a persistent sidebar rail.
 */
import React, { useCallback, useEffect, useState } from 'react';

import AgentFloatingNav from './components/AgentFloatingNav';
import HistorySidebar from './components/HistorySidebar';
import {
  GGJ_AGENT_DELETE_SESSION,
  GGJ_AGENT_HISTORY_REFRESH,
  GGJ_AGENT_NEW_SESSION,
  GGJ_AGENT_SELECT_SESSION,
  GGJ_AGENT_SESSION_CHANGED,
  GGJ_AGENT_TOGGLE_HISTORY,
} from './components/AgentSidebarPanel';
import type { AgentSession } from './types';
import AIAgentPanel from './AIAgentPanel';

export interface AgentHubPageProps {
  onSignOut?: () => void | Promise<void>;
}

const AgentHubPage: React.FC<AgentHubPageProps> = ({ onSignOut }) => {
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rightPanelActive, setRightPanelActive] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024,
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onToggleHistory = () => setHistoryOpen((o) => !o);
    window.addEventListener(GGJ_AGENT_TOGGLE_HISTORY, onToggleHistory);
    return () => window.removeEventListener(GGJ_AGENT_TOGGLE_HISTORY, onToggleHistory);
  }, []);

  useEffect(() => {
    const onSession = (e: Event) => {
      const { sessionId } = (e as CustomEvent<{ sessionId?: string | null }>).detail ?? {};
      setActiveSessionId(sessionId ?? null);
      setHistoryRefreshKey((k) => k + 1);
    };
    const onHistoryRefresh = () => setHistoryRefreshKey((k) => k + 1);
    window.addEventListener(GGJ_AGENT_SESSION_CHANGED, onSession);
    window.addEventListener(GGJ_AGENT_HISTORY_REFRESH, onHistoryRefresh);
    return () => {
      window.removeEventListener(GGJ_AGENT_SESSION_CHANGED, onSession);
      window.removeEventListener(GGJ_AGENT_HISTORY_REFRESH, onHistoryRefresh);
    };
  }, []);

  useEffect(() => {
    const onRightPanel = (e: Event) => {
      const { active } = (e as CustomEvent<{ active?: boolean }>).detail ?? {};
      setRightPanelActive(Boolean(active));
      if (active) setHistoryOpen(false);
    };
    window.addEventListener('ggj:agent-right-panel', onRightPanel);
    return () => window.removeEventListener('ggj:agent-right-panel', onRightPanel);
  }, []);

  const startNewChat = useCallback(() => {
    window.dispatchEvent(new CustomEvent(GGJ_AGENT_NEW_SESSION));
    setHistoryOpen(false);
  }, []);

  const openSession = useCallback((s: AgentSession) => {
    window.dispatchEvent(
      new CustomEvent(GGJ_AGENT_SELECT_SESSION, { detail: { sessionId: s.id } }),
    );
    setHistoryOpen(false);
  }, []);

  const handleSessionDeleted = useCallback((sessionId: string) => {
    window.dispatchEvent(
      new CustomEvent(GGJ_AGENT_DELETE_SESSION, { detail: { sessionId } }),
    );
    setHistoryRefreshKey((k) => k + 1);
  }, []);

  return (
    <div data-tour="page-agent" className="agent-hub-page relative flex flex-1 min-h-0 w-full flex-col overflow-hidden">
      {isDesktop && (
        <AgentFloatingNav
          historyOpen={historyOpen}
          onNewChat={startNewChat}
          onToggleHistory={() => setHistoryOpen((o) => !o)}
        />
      )}

      {historyOpen && (
        <>
          <button
            type="button"
            aria-label="Close chat history"
            className="agent-floating-history-backdrop"
            onClick={() => setHistoryOpen(false)}
          />
          <div className="agent-floating-history-panel" aria-modal="true" role="dialog" aria-label="Chat history">
            <HistorySidebar
              activeSessionId={activeSessionId}
              onSelectSession={openSession}
              onNewSession={startNewChat}
              refreshKey={historyRefreshKey}
              onClose={() => setHistoryOpen(false)}
              onSessionDeleted={handleSessionDeleted}
              floating
            />
          </div>
        </>
      )}

      <div className="flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden">
        <AIAgentPanel embedded hubLayout onSignOut={onSignOut} />
      </div>
    </div>
  );
};

export default AgentHubPage;
